"use client";

import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";

import { search } from "@/actions/search";
import { Facets, PRODUCT_TYPE, SearchParams } from "@/types";

export interface GuidedToolStateStep {
  id: string;
  type: "editorial" | "filter";
  question: string;
  answer?: string | string[] | number | number[];
  filter?: SearchParams;
}

interface GuidedToolContextType {
  state: GuidedToolStateStep[];
  setState: React.Dispatch<React.SetStateAction<GuidedToolStateStep[]>>;
  filters: Facets;
  loading: boolean;
  error: Error | null;
  currentAnswer: string | number | string[] | number[] | undefined;
  setCurrentAnswer: React.Dispatch<React.SetStateAction<string | number | string[] | number[] | undefined>>;
  currentFilter: SearchParams | undefined;
  setCurrentFilter: React.Dispatch<React.SetStateAction<SearchParams | undefined>>;
  activeSlideId: string | undefined;
  setActiveSlideId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const GuidedToolContext = createContext<GuidedToolContextType>({
  state: [],
  setState: () => {},
  filters: {},
  loading: true,
  error: null,
  currentAnswer: "",
  setCurrentAnswer: () => {},
  currentFilter: {},
  setCurrentFilter: () => {},
  activeSlideId: "",
  setActiveSlideId: () => {},
});

export const useGuidedToolContext = () => useContext(GuidedToolContext);

interface GuidedToolProviderProps {
  children: ReactNode;
  modalOpen?: boolean;
}

export const GuidedToolProvider: React.FC<GuidedToolProviderProps> = ({ children, modalOpen }) => {
  const [state, setState] = useState<GuidedToolStateStep[]>([]);
  const [filters, setFilters] = useState<Facets>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // states used for temporary slide states before pushing it to the generic state
  const [currentAnswer, setCurrentAnswer] = useState<string | string[] | number | number[]>();
  const [currentFilter, setCurrentFilter] = useState<SearchParams>();
  const [activeSlideId, setActiveSlideId] = useState<string>();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await search({ withFilters: true, withResults: false, type: PRODUCT_TYPE.PROPERTY_TYPE });

        setFilters(Object.fromEntries(Object.entries(response.filters).filter(([, value]) => Array.isArray(value) && value.length != 0)));
      } catch (error: unknown) {
        setError(error as Error);
      } finally {
        setLoading(false);
      }
    };

    if (modalOpen) {
      fetchData();
    }
  }, [modalOpen]);

  return (
    <GuidedToolContext.Provider
      value={{
        state,
        setState,
        filters,
        loading,
        error,
        currentAnswer,
        setCurrentAnswer,
        currentFilter,
        setCurrentFilter,
        activeSlideId,
        setActiveSlideId,
      }}
    >
      {children}
    </GuidedToolContext.Provider>
  );
};
